/* eslint-disable no-unused-expressions */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  DrawerFooter,
} from '@one-thd/sui-atomic-components';
import { ArrowForward } from '@one-thd/sui-icons';
import { useAssistant, useAssistantStore } from '../../../orchestrator/AssistantOrchestrator';
import { useDrawer } from '../DrawerProvider';
import { shallow } from '../../../store/createStore';
import {
  keyPressMap,
  MESSAGE_ORIGIN_CUSTOM,
} from '../../../shared/constants';
import { redactSensitiveContent, REDACTION_ERROR_MESSAGE } from '../../../shared/utils/redaction';
import { ScrollToBottomButton } from './ScrollToBottomButton';
import { ComposerInput } from '../ComposerInput';

export const DrawerInput = () => {
  const { askUserQuestion } = useAssistant();
  const {
    historyLoading,
    botLoading: botResponseLoading,
    isEscalated,
  } = useAssistantStore((state) => ({
    historyLoading: state.historyLoading,
    botLoading: state.botLoading,
    isEscalated: state.isEscalated,
  }), shallow);

  const { focusInput } = useDrawer();
  const [userMessage, setUserMessage] = useState('');
  const [redactionError, setRedactionError] = useState('');
  const inputRef = useRef(null);

  // Keeps the callbacks below referentially stable — see
  // .claude/context/drawer-layout.md.
  const latest = useRef({});
  latest.current = { userMessage, botResponseLoading, historyLoading, askUserQuestion };

  // React's autoFocus only fires on mount and cannot take focus options, so
  // focus is driven from the ref instead. focusInput={false} disables it entirely.
  useEffect(() => {
    if (!focusInput || !inputRef.current) return;
    inputRef.current.focus({ preventScroll: true });
  }, [focusInput]);

  // Taking the tap over ourselves is what keeps iOS from scrolling the page: the
  // browser's own focus scrolls the input into view, offsetting the visual viewport.
  const handleTouchEnd = useCallback((event) => {
    if (!inputRef.current) return;
    event.preventDefault();
    inputRef.current.focus({ preventScroll: true });
  }, []);

  const clearPrompt = useCallback(() => {
    setUserMessage('');
    setRedactionError('');
  }, []);

  const handleMessageSubmit = useCallback(async () => {
    const {
      userMessage: msg, botResponseLoading: loading, historyLoading: hLoading, askUserQuestion: ask,
    } = latest.current;
    if (msg.trim() === '' || loading || hLoading) return;

    // Clear input optimistically before the async call so users get immediate
    // feedback. If submission is blocked (e.g. redaction), the text is restored.
    const messageToSend = msg;
    setUserMessage('');
    setRedactionError('');

    const submitted = await ask({ userQuestion: messageToSend, messageOrigin: MESSAGE_ORIGIN_CUSTOM });
    if (submitted?.blocked) {
      setUserMessage(submitted.redacted);
      setRedactionError(submitted.error);
      return;
    }
    // preventScroll stops the browser scrolling the input into view, which on iOS
    // offsets the visual viewport and lets the page pan behind the drawer.
    if (inputRef.current) inputRef.current.focus({ preventScroll: true });
  }, []);

  const onUserQuestionChange = useCallback((event) => {
    if (!(keyPressMap.Enter)) {
      const value = event.target.value;
      const { hasSensitive } = redactSensitiveContent(value);
      // Functional update keeps this callback dependency-free (stable). Returning
      // `prev` unchanged when it is already falsy avoids a pointless state update.
      setRedactionError((prev) => {
        if (hasSensitive) return REDACTION_ERROR_MESSAGE;
        return prev ? '' : prev;
      });
      setUserMessage(value);
    }
  }, []);

  const onUserQuestionKeyPress = useCallback((event) => {
    // Keeps track of all currently pressed keys
    keyPressMap[event.key] = event.type === 'keydown';

    if (keyPressMap.Enter) {
      handleMessageSubmit();
    }
  }, [handleMessageSubmit]);

  return (
    <DrawerFooter>
      <ScrollToBottomButton />
      {redactionError && (
        <div className="sui-mb-3">
          <Alert
            status="error"
            onClose={(el) => { el?.currentTarget?.closest('[role="alert"]')?.remove(); }}
          >
            {REDACTION_ERROR_MESSAGE}
          </Alert>
        </div>
      )}
      <div data-testid="input-box" className="sui-flex sui-justify-between sui-items-center sui-gap-2">
        <ComposerInput
          value={userMessage}
          onChange={onUserQuestionChange}
          onKeyDown={onUserQuestionKeyPress}
          onKeyUp={onUserQuestionKeyPress}
          placeholder={isEscalated ? 'Message live agent...' : 'Ask a question'}
          ariaLabel={isEscalated ? 'Live agent textbox' : 'Assistant textbox'}
          inputRef={inputRef}
          onTouchEnd={handleTouchEnd}
          showClear={!!userMessage.length}
          onClear={clearPrompt}
        />
        <div className="sui-grow-0">
          <Button
            variant="primary"
            onClick={handleMessageSubmit}
            id="magic_apron_qa_find_it_for_me"
            key="submit-button"
            aria-label="Submit question"
          >
            <ArrowForward color="inverse" />
          </Button>
        </div>
      </div>
    </DrawerFooter>
  );
};

DrawerInput.propTypes = {
};
